<template>
  <div class="ended-summary">
    <!-- Fetch-level banner: only shown when the /final-stats call failed.
         An empty response (200 with empty arrays) is handled per-card below
         instead, so partial/rolling data still renders. -->
    <div v-if="error" class="ended-summary__banner" :class="{ 'is-permission': errorCode === 403 }">
      {{ errorMessage }}
    </div>

    <!-- Top 5 Participants -->
    <div class="ended-summary__section">
      <div class="ended-summary__section-inner">
        <div class="ended-summary__section-title-row">
          <div class="ended-summary__section-title">{{ $t('endedSummary_TournamentEndedSummary.top5Participants') }}</div>
        </div>
        <div class="ended-summary__divider"></div>
        <!-- Empty state for participants: no cards available (0 results or load error). -->
        <div v-if="!summary.topParticipants.length" class="ended-summary__empty">
          <v-icon size="28" class="ended-summary__empty-icon">fa-regular fa-users</v-icon>
          <div class="ended-summary__empty-text">{{ $t('endedSummary_TournamentEndedSummary.emptyParticipants') }}</div>
        </div>
        <div v-else class="ended-summary__profiles-grid">
          <div
            v-for="(p, i) in summary.topParticipants"
            :key="i"
            class="profile-card"
          >
            <!-- Rank tag + Score -->
            <div class="profile-card__rank-row">
              <div class="profile-card__rank-tag" :style="rankTagStyle(i)">
                {{ ordinalLabel(i + 1) }}
              </div>
              <div class="profile-card__score">{{ $t('endedSummary_TournamentEndedSummary.scorePrefix') }} {{ p.finalScore }}</div>
            </div>
            <!-- Divider -->
            <div class="profile-card__divider"></div>
            <!-- Avatar + Name + Username -->
            <div class="profile-card__header">
              <img
                :src="resolveAvatar(p)"
                class="profile-card__avatar"
                :alt="p.firstName"
              />
              <div class="profile-card__name-block">
                <div class="profile-card__name">{{ p.firstName }} {{ p.lastName }}</div>
                <div class="profile-card__username">@{{ p.username }}</div>
              </div>
            </div>
            <!-- Divider -->
            <div class="profile-card__divider"></div>
            <!-- Extra details -->
            <div class="profile-card__extras">
              <div class="profile-card__extra-item">
                <div class="profile-card__extra-label">{{ $t('endedSummary_TournamentEndedSummary.mostTradedAsset') }}</div>
                <div class="profile-card__asset-tag">{{ p.mostTradedAsset }}</div>
              </div>
              <div class="profile-card__extra-item">
                <div class="profile-card__extra-label">{{ $t('endedSummary_TournamentEndedSummary.trades') }}</div>
                <div class="profile-card__extra-value">{{ p.numberOfTrades }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Bottom row: Assets | Countries | KPI Stats -->
    <div class="ended-summary__bottom-row">
      <!-- Top 5 Assets -->
      <div class="top5-card">
        <div class="top5-header">
          <div class="top5-title">{{ $t('endedSummary_TournamentEndedSummary.top5Assets') }}</div>
          <div
            class="icon-btn"
            :class="{ active: chartView.assets }"
            @click="chartView.assets = !chartView.assets"
            :title="$t('endedSummary_TournamentEndedSummary.toggleChartView')"
          >
            <v-icon size="16">fa-regular fa-chart-pie</v-icon>
          </div>
        </div>
        <div class="top5-divider"></div>

        <!-- Empty state shared by list + chart views -->
        <div v-if="!summary.topAssets.length" class="top5-empty">
          <v-icon size="24" class="top5-empty-icon">fa-regular fa-chart-simple</v-icon>
          <div class="top5-empty-text">{{ $t('endedSummary_TournamentEndedSummary.emptyAssets') }}</div>
        </div>

        <!-- List view -->
        <template v-else-if="!chartView.assets">
          <template v-for="(asset, i) in summary.topAssets">
            <div :key="'ast-' + i" class="top5-item">
              <div class="item-left">
                <div class="rank-badge">{{ i + 1 }}</div>
                <div class="item-name">{{ asset.name }}</div>
              </div>
              <div class="item-value">{{ asset.frequency }} {{ $t('endedSummary_TournamentEndedSummary.tradesSuffix') }}</div>
            </div>
            <div v-if="i < summary.topAssets.length - 1" :key="'adiv-' + i" class="item-divider"></div>
          </template>
        </template>

        <!-- Chart view -->
        <div v-else class="donut-chart-area">
          <apexchart
            type="donut"
            height="160"
            :options="assetsChartOptions"
            :series="assetsChartSeries"
          />
          <div class="donut-list">
            <div v-for="(asset, i) in summary.topAssets" :key="'adot-' + i" class="donut-list-item">
              <div class="donut-item-left">
                <div class="color-dot" :style="{ background: donutColors[i] }"></div>
                <div class="donut-item-name">{{ asset.name }}</div>
              </div>
              <div class="donut-item-value">{{ asset.frequency }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Top 5 Countries -->
      <div class="top5-card">
        <div class="top5-header">
          <div class="top5-title">{{ $t('endedSummary_TournamentEndedSummary.top5Countries') }}</div>
          <div
            class="icon-btn"
            :class="{ active: chartView.countries }"
            @click="chartView.countries = !chartView.countries"
            :title="$t('endedSummary_TournamentEndedSummary.toggleChartView')"
          >
            <v-icon size="16">fa-regular fa-chart-pie</v-icon>
          </div>
        </div>
        <div class="top5-divider"></div>

        <!-- Empty state shared by list + chart views -->
        <div v-if="!summary.topCountries.length" class="top5-empty">
          <v-icon size="24" class="top5-empty-icon">fa-regular fa-earth-americas</v-icon>
          <div class="top5-empty-text">{{ $t('endedSummary_TournamentEndedSummary.emptyCountries') }}</div>
        </div>

        <!-- List view -->
        <template v-else-if="!chartView.countries">
          <template v-for="(country, i) in summary.topCountries">
            <div :key="'cty-' + i" class="top5-item">
              <div class="item-left">
                <div class="rank-badge">{{ i + 1 }}</div>
                <div class="item-name">{{ country.name }}</div>
              </div>
              <div class="item-right">
                <div class="item-value">{{ country.frequency }}%</div>
                <Flag v-if="country.code" :code="country.code" :size="'S'" class="country-flag" />
              </div>
            </div>
            <div v-if="i < summary.topCountries.length - 1" :key="'cdiv-' + i" class="item-divider"></div>
          </template>
        </template>

        <!-- Chart view -->
        <div v-else class="donut-chart-area">
          <apexchart
            type="donut"
            height="160"
            :options="countriesChartOptions"
            :series="countriesChartSeries"
          />
          <div class="donut-list">
            <div v-for="(country, i) in summary.topCountries" :key="'cdot-' + i" class="donut-list-item">
              <div class="donut-item-left">
                <div class="color-dot" :style="{ background: donutColors[i] }"></div>
                <div class="donut-item-name">{{ country.name }}</div>
              </div>
              <div class="donut-item-value">{{ country.frequency }}%</div>
            </div>
          </div>
        </div>
      </div>

      <!-- KPI Stats Column. Show an em-dash when there's no recorded activity
           yet (or the fetch failed) so admins don't read a literal "0" as the
           final number. -->
      <div class="kpi-col">
        <div class="kpi-section">
          <div class="kpi-inner">
            <div class="kpi-label">{{ $t('endedSummary_TournamentEndedSummary.totalParticipants') }}</div>
            <div class="kpi-value" :class="{ 'is-empty': !hasKpiData }">
              {{ hasKpiData ? summary.totalParticipants.toLocaleString() : '—' }}
            </div>
          </div>
        </div>
        <div class="kpi-section">
          <div class="kpi-inner">
            <div class="kpi-label">{{ $t('endedSummary_TournamentEndedSummary.totalTrades') }}</div>
            <div class="kpi-value" :class="{ 'is-empty': !hasKpiData }">
              {{ hasKpiData ? summary.totalTrades.toLocaleString() : '—' }}
            </div>
          </div>
        </div>
        <div class="kpi-section">
          <div class="kpi-inner">
            <div class="kpi-label">{{ $t('endedSummary_TournamentEndedSummary.totalVolume') }}</div>
            <div class="kpi-value" :class="{ 'is-empty': !hasKpiData }">
              <template v-if="hasKpiData">
                {{ summary.totalVolume }} {{ $t('endedSummary_TournamentEndedSummary.lotsSuffix') }}
              </template>
              <template v-else>—</template>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts'
import TournamentRepository from '@/repositories/tournament'

const RANK_STYLES = {
  0: { color: '#B8860B', background: '#FFF6DA' }, // 1st - Gold
  1: { color: '#6B6B6B', background: '#EFEFEF' }, // 2nd - Silver
  2: { color: '#A8572B', background: '#FBEBDC' }, // 3rd - Bronze
}
const RANK_DEFAULT_STYLE = { color: '#6B6B6B', background: '#F5F5F5' } // 4th+

const COUNTRY_CODE_MAP = {
  'argentina': 'ar',
  'bolivia': 'bo',
  'brazil': 'br',
  'canada': 'ca',
  'colombia': 'co',
  'costa rica': 'cr',
  'ecuador': 'ec',
  'germany': 'de',
  'guatemala': 'gt',
  'india': 'in',
  'indonesia': 'id',
  'japan': 'jp',
  'kenya': 'ke',
  'malaysia': 'my',
  'mexico': 'mx',
  'nigeria': 'ng',
  'norway': 'no',
  'panama': 'pa',
  'philippines': 'ph',
  'south africa': 'za',
  'spain': 'es',
  'thailand': 'th',
  'united arab emirates': 'ae',
  'united states': 'us',
  'united states of america': 'us',
  'usa': 'us',
  'uzbekistan': 'uz',
  'viet nam': 'vn',
  'vietnam': 'vn',
}

const EMPTY_SUMMARY = {
  topParticipants: [],
  totalParticipants: 0,
  totalTrades: 0,
  totalVolume: 0,
  topAssets: [],
  topCountries: [],
}

export default {
  name: 'TournamentEndedSummary',

  components: {
    apexchart: VueApexCharts,
  },

  props: {
    tournamentId: { type: Number, default: null },
  },

  data() {
    return {
      loading: false,
      error: null,
      errorCode: null,
      summary: { ...EMPTY_SUMMARY },
      chartView: { assets: false, countries: false },
      donutColors: ['#005931', '#28EA96', '#C3FFE3', '#6366F1', '#ABABAB'],
      defaultAvatar: 'https://upload.wikimedia.org/wikipedia/commons/d/d3/User_Circle.png',
    }
  },

  computed: {
    assetsChartOptions() {
      return {
        chart: { type: 'donut', fontFamily: 'Avenir, sans-serif' },
        colors: this.donutColors,
        labels: this.summary.topAssets.map(a => a.name),
        legend: { show: false },
        dataLabels: { enabled: false },
        plotOptions: { pie: { donut: { size: '55%' } } },
        stroke: { width: 2, colors: ['#fff'] },
      }
    },
    assetsChartSeries() {
      return this.summary.topAssets.map(a => a.frequency)
    },
    countriesChartOptions() {
      return {
        chart: { type: 'donut', fontFamily: 'Avenir, sans-serif' },
        colors: this.donutColors,
        labels: this.summary.topCountries.map(c => c.name),
        legend: { show: false },
        dataLabels: { enabled: false },
        plotOptions: { pie: { donut: { size: '55%' } } },
        stroke: { width: 2, colors: ['#fff'] },
      }
    },
    countriesChartSeries() {
      return this.summary.topCountries.map(c => c.frequency)
    },
    // Any non-zero KPI — hides the "—" placeholder once the backend reports
    // real activity, even if the top5 arrays are still empty.
    hasKpiData() {
      return (
        Number(this.summary.totalParticipants) > 0
        || Number(this.summary.totalTrades) > 0
        || parseFloat(String(this.summary.totalVolume).replace(/,/g, '')) > 0
      )
    },
    errorMessage() {
      if (!this.error) return ''
      if (this.errorCode === 403) return this.$t('endedSummary_TournamentEndedSummary.permissionDenied')
      return this.error
    },
  },

  watch: {
    tournamentId: {
      immediate: true,
      handler(newId) {
        if (newId) this.fetchStats()
      },
    },
  },

  methods: {
    ordinalLabel(n) {
      return this.$t('endedSummary_TournamentEndedSummary.ordinalPlace', { n })
    },
    rankTagStyle(index) {
      const style = RANK_STYLES[index] || RANK_DEFAULT_STYLE
      return { color: style.color, backgroundColor: style.background }
    },
    async fetchStats() {
      if (!this.tournamentId) return
      this.loading = true
      this.error = null
      this.errorCode = null
      try {
        const res = await TournamentRepository.getTournamentFinalStats(this.tournamentId)
        this.summary = this.mapResponse(res && res.data ? res.data : {})
      } catch (err) {
        console.error('[TournamentEndedSummary] Failed to load final stats', err)
        // Capture HTTP status so the banner can differentiate 403 (no
        // permission) from a generic fetch failure. Per-card empty states
        // render regardless — the banner just explains *why* they're empty.
        this.errorCode = err?.response?.status || null
        this.error = this.$t('endedSummary_TournamentEndedSummary.loadError')
        this.summary = { ...EMPTY_SUMMARY }
      } finally {
        this.loading = false
      }
    },
    mapResponse(data) {
      const total = Number(data.totalParticipants) || 0
      return {
        totalParticipants: total,
        totalTrades: Number(data.totalTrades) || 0,
        totalVolume: this.formatVolume(data.totalVolume),
        topParticipants: (data.top5Participants || []).map(p => {
          const fullName = (p.fullName || '').trim()
          const parts = fullName.split(/\s+/)
          const firstName = parts[0] || ''
          const lastName = parts.slice(1).join(' ')
          return {
            firstName,
            lastName,
            username: p.username || '',
            country: p.country || '',
            // Prefer the picture from the /final-stats response when the API starts returning one.
            // Until then, fall back to the participants list in the store (resolved in `resolveAvatar`).
            profilePicture: p.profilePicture || null,
            tradingAccountIdentifier: p.tradingAccountIdentifier || '',
            finalScore: this.formatScore(p.finalScore),
            numberOfTrades: Number(p.numberOfTrades) || 0,
            mostTradedAsset: p.mostTradedAsset || '-',
          }
        }),
        topAssets: (data.top5Assets || []).map(a => ({
          name: a.assetName || '-',
          frequency: Number(a.frequency) || 0,
        })),
        topCountries: (data.top5Countries || []).map(c => ({
          name: c.country || '-',
          code: this.countryToCode(c.country),
          frequency: total ? Math.round((Number(c.frequency) / total) * 100) : 0,
        })),
      }
    },
    formatScore(score) {
      if (score == null || isNaN(score)) return '0'
      return Number(score).toLocaleString(undefined, { maximumFractionDigits: 2 })
    },
    formatVolume(volume) {
      if (volume == null || isNaN(volume)) return '0'
      const n = Number(volume)
      return n % 1 === 0 ? n.toLocaleString() : n.toLocaleString(undefined, { maximumFractionDigits: 2 })
    },
    countryToCode(name) {
      if (!name) return ''
      const code = COUNTRY_CODE_MAP[name.toLowerCase().trim()]
      return code || ''
    },
    // Resolve a participant's avatar, falling back to the store's participants list when the
    // /final-stats response doesn't include a profilePicture. Match by brokerAccountIdentifier
    // first (most reliable), then username.
    resolveAvatar(p) {
      if (p.profilePicture) return p.profilePicture
      const participants = this.$store.state.tournament?.participants || []
      if (!participants.length) return this.defaultAvatar
      const match = participants.find(x =>
        (p.tradingAccountIdentifier && x.brokerAccountIdentifier === p.tradingAccountIdentifier)
        || (p.username && x.username === p.username)
      )
      return (match && match.profilePicture) || this.defaultAvatar
    },
  },
}
</script>

<style lang="scss" scoped>
.ended-summary {
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
}

// ── Top 5 Participants ──────────────────────────────────────────────────────

.ended-summary__section {
  border: 1px solid #9a9a9a;
  border-radius: 10px;
  padding: 20px;
}

.ended-summary__section-inner {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.ended-summary__section-title {
  font-family: 'Aeonik', sans-serif !important;
  font-weight: 500 !important;
  font-size: 18px !important;
  color: #000 !important;
  line-height: 1.5;
}

.ended-summary__section-title-row {
  display: flex;
  align-items: center;
}

.ended-summary__divider {
  width: 100%;
  height: 1px;
  background: #ebebeb;
}

.ended-summary__profiles-grid {
  display: flex;
  gap: 10px;
  width: 100%;
}

// ── Banner + empty states ───────────────────────────────────────────────────

.ended-summary__banner {
  padding: 10px 14px;
  border-radius: 8px;
  border: 1px solid #ebebeb;
  background: #fafafa;
  color: #3b3b3b;
  font-family: 'Avenir', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 1.5;

  &.is-permission {
    background: #FFF6DA;
    border-color: #E9C96B;
    color: #7A5A00;
  }
}

.ended-summary__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 32px 16px;
  color: #ababab;
}

.ended-summary__empty-icon {
  color: #ababab !important;
}

.ended-summary__empty-text {
  font-family: 'Avenir', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 1.5;
  text-align: center;
}

.top5-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 20px 8px;
  color: #ababab;
  flex: 1;
}

.top5-empty-icon {
  color: #ababab !important;
}

.top5-empty-text {
  font-family: 'Avenir', sans-serif;
  font-weight: 500;
  font-size: 13px;
  line-height: 1.5;
  text-align: center;
}

.kpi-value.is-empty {
  color: #ababab !important;
}

// ── Profile Card ─────────────────────────────────────────────────────────────

.profile-card {
  flex: 1;
  border: 1px solid #ebebeb;
  border-radius: 5px;
  padding: 10px 16px;
  display: flex;
  flex-direction: column;
  gap: 5px;
  min-width: 0;
}

.profile-card__rank-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.profile-card__rank-tag {
  font-family: 'Avenir', sans-serif;
  font-weight: 700;
  font-size: 14px;
  line-height: 1.5;
  padding: 3px 10px;
  border-radius: 18px;
  background: #f5f5f5;
  white-space: nowrap;
}

.profile-card__score {
  font-family: 'Avenir', sans-serif;
  font-weight: 600;
  font-size: 14px;
  line-height: 1.5;
  color: #3b3b3b;
  letter-spacing: 0.28px;
  text-align: right;
  flex: 1;
  min-width: 0;
}

.profile-card__divider {
  width: 100%;
  height: 1px;
  background: #ebebeb;
  flex-shrink: 0;
}

.profile-card__header {
  display: flex;
  align-items: center;
  gap: 12px;
}

.profile-card__avatar {
  width: 43px;
  height: 43px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
}

.profile-card__name-block {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
}

.profile-card__name {
  font-family: 'Avenir', sans-serif;
  font-weight: 350;
  font-size: 16px;
  line-height: 1.5;
  color: #1b1b1b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.profile-card__username {
  font-family: 'Avenir', sans-serif;
  font-weight: 300;
  font-size: 12px;
  line-height: 1.5;
  color: #3b3b3b;
  letter-spacing: 0.24px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.profile-card__extras {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.profile-card__extra-item {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.profile-card__extra-label {
  font-family: 'Avenir', sans-serif;
  font-weight: 600;
  font-size: 14px;
  line-height: 1.5;
  color: #3b3b3b;
  letter-spacing: 0.28px;
}

.profile-card__asset-tag {
  font-family: 'Avenir', sans-serif;
  font-weight: 300;
  font-size: 14px;
  line-height: 1.5;
  color: #3b3b3b;
  background: #f5f5f5;
  border-radius: 18px;
  padding: 3px 12px;
  border: 1px solid #ebebeb;
  margin-top: 2px;
  align-self: flex-start;
  white-space: nowrap;
}

.profile-card__extra-value {
  font-family: 'Avenir', sans-serif;
  font-weight: 600;
  font-size: 14px;
  line-height: 1.5;
  color: #1b1b1b;
}

// ── Bottom Row ────────────────────────────────────────────────────────────────

.ended-summary__bottom-row {
  display: flex;
  gap: 20px;
  width: 100%;
  align-items: stretch;
}

// ── Top 5 Cards (shared with IB Dashboard style) ─────────────────────────────

.top5-card {
  flex: 1;
  border: 1px solid #9a9a9a;
  border-radius: 10px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
}

.top5-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.top5-title {
  font-family: 'Aeonik', sans-serif !important;
  font-weight: 500 !important;
  font-size: 18px !important;
  line-height: 1.5;
  color: #000 !important;
}

.icon-btn {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 5px;
  cursor: pointer;
  transition: background-color 0.15s ease;
  flex-shrink: 0;

  &.active {
    background: #28ea96;
  }

  &:hover:not(.active) {
    background: rgba(0, 0, 0, 0.04);
  }
}

.top5-divider {
  width: 100%;
  height: 1px;
  background: #ebebeb;
}

.top5-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.item-left {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
}

.item-right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.rank-badge {
  width: 24px;
  height: 24px;
  background: #c3ffe3;
  border-radius: 5px;
  font-family: 'Avenir', sans-serif;
  font-weight: 350;
  font-size: 14px;
  line-height: 24px;
  color: #005931;
  text-align: center;
  flex-shrink: 0;
}

.item-name {
  font-family: 'Avenir', sans-serif;
  font-weight: 350;
  font-size: 16px;
  line-height: 1.5;
  color: #14161b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.item-value {
  font-family: 'Avenir', sans-serif;
  font-weight: 300;
  font-size: 14px;
  line-height: 1.5;
  color: #ababab;
  text-align: right;
  white-space: nowrap;
}

.item-divider {
  width: 100%;
  height: 1px;
  background: #ebebeb;
}

.country-flag {
  flex-shrink: 0;
}

// Chart views
.donut-chart-area {
  display: flex;
  gap: 16px;
  align-items: center;
  flex: 1;
}

.donut-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
  min-width: 0;
}

.donut-list-item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
}

.donut-item-left {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.color-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  flex-shrink: 0;
}

.donut-item-name {
  font-family: 'Avenir', sans-serif;
  font-weight: 500;
  font-size: 13px;
  line-height: 1.5;
  color: #3b3b3b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.donut-item-value {
  font-family: 'Avenir', sans-serif;
  font-weight: 500;
  font-size: 13px;
  color: #ababab;
  text-align: right;
  flex-shrink: 0;
}

// ── KPI Stats Column ─────────────────────────────────────────────────────────

.kpi-col {
  display: flex;
  flex-direction: column;
  width: 244px;
  flex-shrink: 0;
  gap: 12px;
}

.kpi-section {
  flex: 1;
  padding: 10px;
  display: flex;
  align-items: stretch;
  border: 1px solid #9a9a9a;
  border-radius: 10px;
}

.kpi-inner {
  display: flex;
  flex-direction: column;
  gap: 4px;
  justify-content: center;
  width: 100%;
}

.kpi-label {
  font-family: 'Avenir', sans-serif;
  font-weight: 300;
  font-size: 14px;
  line-height: 1.5;
  color: #ababab;
}

.kpi-value {
  font-family: 'Aeonik', sans-serif !important;
  font-weight: 700 !important;
  font-size: 30px !important;
  line-height: 1.2;
  color: #14161b !important;
}

// ── Responsive ───────────────────────────────────────────────────────────────

@media (max-width: 1100px) {
  .ended-summary__profiles-grid {
    flex-wrap: wrap;
  }
  .profile-card {
    flex: 1 1 calc(33% - 10px);
    min-width: 160px;
  }
  .ended-summary__bottom-row {
    flex-wrap: wrap;
  }
  .top5-card {
    flex: 1 1 calc(50% - 10px);
    min-width: 240px;
  }
  .kpi-col {
    width: 100%;
    flex-direction: row;
  }
}

@media (max-width: 768px) {
  .ended-summary__profiles-grid {
    flex-direction: column;
  }
  .top5-card {
    flex: 1 1 100%;
  }
  .kpi-col {
    flex-direction: column;
    width: 100%;
  }
}
</style>

<!-- Dark mode overrides (unscoped so .theme--dark ancestor selector works) -->
<style lang="scss">
.theme--dark .ended-summary {
  // ── Section cards ──────────────────────────────────────────────────────────
  .ended-summary__section {
    border-color: #555 !important;
  }

  .ended-summary__section-title {
    color: #E0E0E0 !important;
  }

  .ended-summary__divider {
    background: rgba(255, 255, 255, 0.12) !important;
  }

  // ── Profile cards ──────────────────────────────────────────────────────────
  .profile-card {
    border-color: #555 !important;
  }

  .profile-card__rank-tag {
    background: #444 !important;
  }

  .profile-card__score {
    color: #BDBDBD !important;
  }

  .profile-card__divider {
    background: rgba(255, 255, 255, 0.12) !important;
  }

  .profile-card__name {
    color: #E0E0E0 !important;
  }

  .profile-card__username {
    color: #BDBDBD !important;
  }

  .profile-card__extra-label {
    color: #BDBDBD !important;
  }

  .profile-card__asset-tag {
    background: #444 !important;
    border-color: #555 !important;
    color: #E0E0E0 !important;
  }

  .profile-card__extra-value {
    color: #E0E0E0 !important;
  }

  // ── Top 5 cards (Assets / Countries) ───────────────────────────────────────
  .top5-card {
    border-color: #555 !important;
  }

  .top5-title {
    color: #E0E0E0 !important;
  }

  .top5-divider,
  .item-divider {
    background: rgba(255, 255, 255, 0.12) !important;
  }

  .item-name {
    color: #E0E0E0 !important;
  }

  .icon-btn:hover:not(.active) {
    background: rgba(255, 255, 255, 0.08) !important;
  }

  .donut-item-name {
    color: #BDBDBD !important;
  }

  // ── Rank badges ────────────────────────────────────────────────────────────
  .rank-badge {
    background: #1a4d35 !important;
    color: #6efcb4 !important;
  }

  // ── Gray text → lighter for readability ────────────────────────────────────
  .item-value,
  .donut-item-value {
    color: #BDBDBD !important;
  }

  .kpi-label {
    color: #BDBDBD !important;
  }

  // ── KPI cards ──────────────────────────────────────────────────────────────
  .kpi-section {
    border-color: #555 !important;
  }

  .kpi-value {
    color: #E0E0E0 !important;
  }

  .kpi-value.is-empty {
    color: #8a8a8a !important;
  }

  // ── Banner + empty states ────────────────────────────────────────────────
  .ended-summary__banner {
    background: #2a2a2a !important;
    border-color: #555 !important;
    color: #E0E0E0 !important;

    &.is-permission {
      background: #3a2f10 !important;
      border-color: #7a5e1a !important;
      color: #F5DB8A !important;
    }
  }

  .ended-summary__empty,
  .top5-empty {
    color: #8a8a8a !important;
  }

  .ended-summary__empty-icon,
  .top5-empty-icon {
    color: #8a8a8a !important;
  }
}
</style>
